import React, { useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AuthContext from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { subscribeUserToPush } from '../utils/pushConfig';
import { getFileUrl } from '../utils/fileUtil';
import axios from 'axios';

const Dashboard = () => {
  const { currentUser, isAdmin, token } = useContext(AuthContext);
  const [boards, setBoards] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(() => localStorage.getItem('dashboardView') || 'workspaces');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [boardsRes, teamsRes] = await Promise.all([
          axios.get('/api/boards', { headers: { 'x-auth-token': token } }),
          axios.get('/api/workspaces', { headers: { 'x-auth-token': token } })
        ]);
        setBoards(boardsRes.data);
        setTeams(teamsRes.data);
        subscribeUserToPush();
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchData();
  }, [token]);

  const switchView = (v) => {
    setView(v);
    localStorage.setItem('dashboardView', v);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-blue"></div>
      </div>
    );
  }

  // Group boards by team/workspace for the Workspaces view
  const activeBoards = boards.filter(b => !b.isArchived);
  const archivedBoards = boards.filter(b => b.isArchived);
  const pinnedBoards = activeBoards.filter(b => b.isPinned);
  const unpinnedBoards = activeBoards.filter(b => !b.isPinned);
  const teamIds = teams.map(t => t._id);
  const groupedBoards = teams.map(team => ({
    ...team,
    teamBoards: unpinnedBoards.filter(b => b.workspace === team._id || (b.workspace?._id === team._id))
  }));
  const otherBoards = unpinnedBoards.filter(board => {
    const boardWorkspaceId = board.workspace?._id || board.workspace;
    return !teamIds.includes(boardWorkspaceId);
  });

  const handleUnarchive = async (boardId) => {
    try {
      await axios.put(`/api/boards/${boardId}`, { isArchived: false }, { headers: { 'x-auth-token': token } });
      setBoards(prev => prev.map(b => (b._id === boardId ? { ...b, isArchived: false } : b)));
    } catch (e) {
      console.error('Error unarchiving board:', e);
    }
  };

  return (
    <div className="max-w-7xl mx-auto py-8 px-4 font-sans text-gray-900 dark:text-gray-100">
      <div className="flex flex-wrap gap-4 justify-between items-center mb-10">
        <div>
          <h1 className="text-3xl font-black text-gray-900 dark:text-gray-100 tracking-tighter uppercase">Project Dashboard</h1>
          <p className="text-sm font-bold text-gray-400 dark:text-gray-500 mt-1 uppercase tracking-widest">Active Workspace for {currentUser.name}</p>
        </div>

        <div className="flex items-center space-x-3">
          {/* View toggle */}
          <div className="inline-flex bg-gray-100 dark:bg-gray-800 rounded-xl p-1">
            {[['workspaces', 'Workspaces'], ['tasks', 'All Tasks']].map(([key, label]) => (
              <button
                key={key}
                onClick={() => switchView(key)}
                className={`px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${
                  view === key
                    ? 'bg-white dark:bg-gray-900 text-primary-blue shadow'
                    : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <Link
            to="/teams?create=true"
            className="px-6 py-2.5 bg-secondary-cyan text-white rounded-xl hover:bg-cyan-600 transition-all shadow-lg font-black text-xs uppercase tracking-widest"
          >
            + New Workspace
          </Link>
          {isAdmin && (
            <Link
              to="/boards/new"
              className="px-6 py-2.5 bg-primary-blue text-white rounded-xl hover:bg-blue-700 transition-all shadow-lg font-black text-xs uppercase tracking-widest"
            >
              + New Board
            </Link>
          )}
        </div>
      </div>

      {view === 'tasks' ? (
        <MasterList token={token} boards={boards} />
      ) : (
        <WorkspacesView groupedBoards={groupedBoards} otherBoards={otherBoards} teams={teams} boards={activeBoards} pinnedBoards={pinnedBoards} archivedBoards={archivedBoards} onUnarchive={handleUnarchive} />
      )}
    </div>
  );
};

const WorkspacesView = ({ groupedBoards, otherBoards, teams, boards, pinnedBoards, archivedBoards, onUnarchive }) => (
  <>
    {pinnedBoards.map(board => (
      <PinnedHero key={board._id} board={board} />
    ))}

    {groupedBoards.map(team => (
      <div key={team._id} className="mb-12 animate-fade-in">
        <div className="flex items-center space-x-3 mb-6">
          <div className="bg-white dark:bg-gray-800 p-2.5 rounded-xl shadow-md border border-gray-100 dark:border-gray-700 flex items-center justify-center">
            <span className="text-xl">👥</span>
          </div>
          <div>
            <h2 className="text-xl font-black text-gray-800 dark:text-gray-100 uppercase tracking-tight">{team.name}</h2>
            <p className="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-[0.2em] font-black">Shared Team Workspace</p>
          </div>
        </div>

        {team.teamBoards.length === 0 ? (
          <div className="bg-gray-50/50 dark:bg-gray-900/30 rounded-2xl p-8 border-2 border-dashed border-gray-200 dark:border-gray-800 text-center">
            <p className="text-gray-400 dark:text-gray-500 text-xs font-bold uppercase tracking-widest">No active boards in this team</p>
            <Link to="/boards/new" className="text-primary-blue dark:text-blue-400 text-[10px] font-black uppercase tracking-tighter hover:underline mt-2 inline-block">Create the first one</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {team.teamBoards.map(board => (
              <BoardCard key={board._id} board={board} />
            ))}
          </div>
        )}
      </div>
    ))}

    {otherBoards.length > 0 && (
      <div className="mb-12 animate-fade-in">
        <div className="flex items-center space-x-3 mb-6">
          <div className="bg-white dark:bg-gray-800 p-2.5 rounded-xl shadow-md border border-gray-100 dark:border-gray-700 flex items-center justify-center">
            <span className="text-xl">🚀</span>
          </div>
          <div>
            <h2 className="text-xl font-black text-gray-800 dark:text-gray-100 uppercase tracking-tight">Other Projects</h2>
            <p className="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-[0.2em] font-black">Personal or Shared Boards</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {otherBoards.map(board => (
            <BoardCard key={board._id} board={board} />
          ))}
        </div>
      </div>
    )}

    {archivedBoards.length > 0 && (
      <ArchivedSection archivedBoards={archivedBoards} onUnarchive={onUnarchive} />
    )}

    {teams.length === 0 && boards.length === 0 && (
      <div className="bg-white dark:bg-gray-800 shadow-2xl rounded-3xl p-20 text-center border border-gray-100 dark:border-gray-700 animate-fade-in">
        <div className="text-6xl mb-8">🏗️</div>
        <h2 className="text-3xl font-black text-gray-900 dark:text-white mb-3 uppercase tracking-tighter">Ready for liftoff?</h2>
        <p className="text-gray-500 dark:text-gray-400 max-w-sm mx-auto mb-10 font-medium">Start by creating a team or your first project board to begin collaborating.</p>
        <div className="flex justify-center space-x-4">
          <Link to="/teams?create=true" className="px-8 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-xl font-black uppercase text-xs tracking-widest text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all">Setup Team</Link>
          <Link to="/boards/new" className="px-8 py-3 bg-primary-blue text-white rounded-xl font-black uppercase text-xs tracking-widest hover:bg-blue-700 transition-all shadow-lg">New Board</Link>
        </div>
      </div>
    )}
  </>
);

const PinnedHero = ({ board }) => {
  const heroStyle = board.background?.type === 'image'
    ? { backgroundImage: `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.55)), url(${board.background.value})` }
    : {};
  return (
    <div className="mb-12 animate-fade-in">
      <Link
        to={`/boards/${board.slug || board._id}`}
        style={heroStyle}
        className="group relative block rounded-3xl overflow-hidden shadow-2xl border border-gray-800 bg-gradient-to-br from-gray-900 via-gray-800 to-primary-blue/40 bg-cover bg-center p-8 md:p-10 text-white hover:-translate-y-1 transition-all"
      >
        <div className="relative z-10 flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] font-black text-white/60 mb-2">📌 Pinned &middot; Personal HQ</p>
            <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tighter group-hover:underline">{board.name}</h2>
            {board.description && (
              <p className="text-xs mt-2 text-white/70 font-bold italic max-w-xl">"{board.description}"</p>
            )}
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex flex-col text-right">
              <span className="text-[9px] uppercase font-black tracking-widest text-white/50">Phases</span>
              <span className="text-lg font-black uppercase tracking-tighter">{board.columns?.length || 0}</span>
            </div>
            <div className="h-12 w-12 rounded-2xl bg-white/15 flex items-center justify-center backdrop-blur-md border border-white/30 transition-all duration-300 transform group-hover:rotate-12 group-hover:bg-white/25">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M9 5l7 7-7 7"></path></svg>
            </div>
          </div>
        </div>
        <div className="absolute -right-8 -bottom-10 text-[9rem] opacity-10 select-none pointer-events-none">🎛️</div>
      </Link>
    </div>
  );
};

const ArchivedSection = ({ archivedBoards, onUnarchive }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-12 animate-fade-in">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center space-x-3 mb-6 w-full text-left group"
      >
        <div className="bg-white dark:bg-gray-800 p-2.5 rounded-xl shadow-md border border-gray-100 dark:border-gray-700 flex items-center justify-center">
          <span className="text-xl">📦</span>
        </div>
        <div>
          <h2 className="text-xl font-black text-gray-400 dark:text-gray-500 uppercase tracking-tight group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors">
            Archived
          </h2>
          <p className="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-[0.2em] font-black">
            {archivedBoards.length} board{archivedBoards.length === 1 ? '' : 's'} &middot; click to {open ? 'collapse' : 'expand'}
          </p>
        </div>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M9 5l7 7-7 7"></path>
        </svg>
      </button>

      {open && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {archivedBoards.map(board => (
            <div key={board._id} className="relative">
              <div className="opacity-60 grayscale hover:opacity-90 hover:grayscale-0 transition-all">
                <BoardCard board={board} />
              </div>
              <button
                onClick={() => onUnarchive(board._id)}
                className="absolute top-2 left-2 z-20 px-2.5 py-1 bg-white/90 dark:bg-gray-900/90 text-gray-600 dark:text-gray-300 text-[9px] font-black uppercase tracking-widest rounded-lg border border-gray-200 dark:border-gray-700 shadow hover:bg-primary-blue hover:text-white transition-all"
              >
                Unarchive
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const PRIORITY_STYLES = {
  Urgent: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  High: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  Medium: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  Low: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
};

const isDoneColumn = (col) => /^(done|complete|completed|archived)$/i.test((col || '').trim());

const PRIORITY_RANK = { Urgent: 0, High: 1, Medium: 2, Low: 3 };

const sortCards = (arr, sortBy) => {
  if (sortBy === 'due') {
    return [...arr].sort((a, b) => {
      const da = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
      const db = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
      return da - db; // soonest/overdue first, no-date last
    });
  }
  if (sortBy === 'priority') {
    return [...arr].sort((a, b) => (PRIORITY_RANK[a.priority] ?? 9) - (PRIORITY_RANK[b.priority] ?? 9));
  }
  return arr;
};

const MasterList = ({ token, boards }) => {
  const socket = useSocket();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [hideDone, setHideDone] = useState(true);
  const [hideSubtasks, setHideSubtasks] = useState(true);
  const [groupByBoard, setGroupByBoard] = useState(true);
  const [sortBy, setSortBy] = useState('due'); // 'due' | 'priority' | 'none'

  const reload = useCallback(async () => {
    try {
      const res = await axios.get('/api/cards/all', { headers: { 'x-auth-token': token } });
      setCards(res.data);
      setError(null);
    } catch (e) {
      setError('Could not load tasks.');
    }
  }, [token]);

  // Initial load
  useEffect(() => {
    if (token) reload().finally(() => setLoading(false));
  }, [token, reload]);

  // Live updates: join every board's room and refetch (debounced) on any card change,
  // so agent/teammate additions appear without a manual refresh.
  useEffect(() => {
    if (!socket || !boards || boards.length === 0) return;
    const ids = boards.map(b => b._id);
    const join = () => ids.forEach(id => socket.emit('join:board', id));
    join();
    let timer;
    const onChange = () => { clearTimeout(timer); timer = setTimeout(reload, 400); };
    socket.on('card:created', onChange);
    socket.on('card:updated', onChange);
    socket.on('card:deleted', onChange);
    socket.on('connect', join); // rejoin after a reconnect
    return () => {
      clearTimeout(timer);
      socket.off('card:created', onChange);
      socket.off('card:updated', onChange);
      socket.off('card:deleted', onChange);
      socket.off('connect', join);
      ids.forEach(id => socket.emit('leave:board', id));
    };
  }, [socket, boards, reload]);

  const patchCard = (id, patch) => setCards(prev => prev.map(c => (c._id === id ? { ...c, ...patch } : c)));
  const removeCard = (id) => setCards(prev => prev.filter(c => c._id !== id));

  const toggleComplete = async (card) => {
    const newVal = card.completedAt ? null : new Date().toISOString();
    patchCard(card._id, { completedAt: newVal }); // optimistic
    try {
      const res = await axios.put(`/api/cards/${card._id}`, { completedAt: newVal }, { headers: { 'x-auth-token': token } });
      const d = res.data || {};
      if (d.isArchived) removeCard(card._id);
      else patchCard(card._id, { completedAt: d.completedAt ?? newVal, column: d.column ?? card.column });
    } catch (e) {
      patchCard(card._id, { completedAt: card.completedAt }); // revert
    }
  };

  const setDue = async (card, dateStr) => {
    const val = dateStr || null;
    patchCard(card._id, { dueDate: val }); // optimistic
    try {
      await axios.put(`/api/cards/${card._id}`, { dueDate: val }, { headers: { 'x-auth-token': token } });
    } catch (e) {
      patchCard(card._id, { dueDate: card.dueDate }); // revert
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return cards.filter(c => {
      // c.done is computed server-side (completedAt, completion target column, or done-like name).
      if (hideDone && (c.done || isDoneColumn(c.column) || c.completedAt)) return false;
      if (hideSubtasks && c.isChild) return false;
      if (!q) return true;
      return [c.title, c.description, c.column, c.board?.name, c.workspace, ...(c.assignees || [])]
        .filter(Boolean)
        .some(v => v.toLowerCase().includes(q));
    });
  }, [cards, search, hideDone, hideSubtasks]);

  const groups = useMemo(() => {
    if (!groupByBoard) return [['', sortCards(filtered, sortBy)]];
    const map = new Map();
    for (const c of filtered) {
      const key = `${c.workspace} / ${c.board?.name}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    }
    return Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([k, rows]) => [k, sortCards(rows, sortBy)]);
  }, [filtered, groupByBoard, sortBy]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-48">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-blue"></div>
      </div>
    );
  }

  if (error) {
    return <div className="text-center text-red-500 font-bold py-12">{error}</div>;
  }

  return (
    <div className="animate-fade-in">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search tasks, boards, people..."
          className="flex-1 min-w-[220px] px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-blue"
        />
        <label className="flex items-center space-x-2 text-xs font-black uppercase tracking-widest text-gray-500 dark:text-gray-400 cursor-pointer select-none">
          <input type="checkbox" checked={hideDone} onChange={e => setHideDone(e.target.checked)} className="rounded" />
          <span>Hide Done</span>
        </label>
        <label className="flex items-center space-x-2 text-xs font-black uppercase tracking-widest text-gray-500 dark:text-gray-400 cursor-pointer select-none">
          <input type="checkbox" checked={hideSubtasks} onChange={e => setHideSubtasks(e.target.checked)} className="rounded" />
          <span>Hide subtasks</span>
        </label>
        <label className="flex items-center space-x-2 text-xs font-black uppercase tracking-widest text-gray-500 dark:text-gray-400 cursor-pointer select-none">
          <input type="checkbox" checked={groupByBoard} onChange={e => setGroupByBoard(e.target.checked)} className="rounded" />
          <span>Group by board</span>
        </label>
        <div className="flex items-center space-x-2">
          <span className="text-xs font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">Sort</span>
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            className="text-xs font-bold rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-blue"
          >
            <option value="due">Due date</option>
            <option value="priority">Priority</option>
            <option value="none">Default</option>
          </select>
        </div>
        <span className="text-xs font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 ml-auto">
          {filtered.length} task{filtered.length === 1 ? '' : 's'}
        </span>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-gray-50/50 dark:bg-gray-900/30 rounded-2xl p-12 border-2 border-dashed border-gray-200 dark:border-gray-800 text-center">
          <p className="text-gray-400 dark:text-gray-500 text-xs font-bold uppercase tracking-widest">No tasks match.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-900 text-left">
                <th className="px-3 py-3 w-8"></th>
                {(!groupByBoard ? ['Workspace / Board', 'Column', 'Task', 'Details'] : ['Column', 'Task', 'Details'])
                  .map(h => (
                    <th key={h} className="px-4 py-3 text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 whitespace-nowrap">{h}</th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {groups.map(([groupName, rows]) => (
                <React.Fragment key={groupName || 'all'}>
                  {groupByBoard && (
                    <tr className="bg-gray-100/70 dark:bg-gray-800/60">
                      <td colSpan={4} className="px-4 py-2 text-xs font-black uppercase tracking-widest text-gray-600 dark:text-gray-300">
                        {groupName} <span className="text-gray-400 dark:text-gray-500">({rows.length})</span>
                      </td>
                    </tr>
                  )}
                  {rows.map(c => (
                    <TaskRow key={c._id} c={c} showBoard={!groupByBoard} onToggleComplete={toggleComplete} onSetDue={setDue} />
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const toDateInput = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return new Date(dt.getTime() - dt.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

const TaskRow = ({ c, showBoard, onToggleComplete, onSetDue }) => {
  const [showDue, setShowDue] = useState(false);
  const completed = !!c.completedAt;
  const due = c.dueDate ? new Date(c.dueDate) : null;
  const overdue = due && !completed && !isDoneColumn(c.column) && due < new Date(new Date().toDateString());
  const link = `/boards/${c.board?.slug || c.board?._id}?cardId=${c._id}`;

  return (
    <tr className="border-t border-gray-100 dark:border-gray-800 hover:bg-blue-50/40 dark:hover:bg-gray-800/40 transition-colors">
      {/* Quick action: mark complete */}
      <td className="px-3 py-3 align-top">
        <button
          onClick={() => onToggleComplete(c)}
          title={completed ? 'Mark incomplete' : 'Mark as complete'}
          className={`h-5 w-5 rounded-full border-2 flex items-center justify-center transition-colors ${
            completed
              ? 'bg-green-500 border-green-500 text-white'
              : 'border-gray-300 dark:border-gray-600 hover:border-green-500'
          }`}
        >
          {completed && (
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
          )}
        </button>
      </td>
      {showBoard && (
        <td className="px-4 py-3 align-top whitespace-nowrap">
          <div className="font-black text-xs uppercase tracking-tight text-gray-700 dark:text-gray-200">{c.board?.name}</div>
          <div className="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-widest">{c.workspace}</div>
        </td>
      )}
      <td className="px-4 py-3 align-top whitespace-nowrap">
        <span className={`inline-block px-2.5 py-1 rounded-lg text-[11px] font-bold ${c.orphaned ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'}`}>{c.column}</span>
        {c.orphaned && (
          <div className="text-[9px] text-amber-600 dark:text-amber-400 font-bold uppercase tracking-wide mt-1" title="This column was removed from the board, so the card has no lane on the board. Open it to reassign a column.">⚠ column removed</div>
        )}
      </td>
      <td className="px-4 py-3 align-top max-w-md">
        <Link to={link} className={`font-bold hover:text-primary-blue dark:hover:text-blue-400 hover:underline ${completed ? 'line-through text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-gray-100'}`}>
          {c.isChild && <span className="text-gray-400" title="Subtask card">↳ </span>}
          {c.title}
        </Link>
        {c.description && (
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 line-clamp-1">{c.description.replace(/[#*_`>[\]]/g, '')}</p>
        )}
      </td>
      <td className="px-4 py-3 align-top">
        <div className="flex flex-wrap items-center gap-1.5">
          {c.priority && (
            <span className={`px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wider ${PRIORITY_STYLES[c.priority] || PRIORITY_STYLES.Low}`}>{c.priority}</span>
          )}
          {/* Due date: click to edit; quick "+ Due" when unset */}
          {showDue ? (
            <input
              type="date"
              autoFocus
              defaultValue={toDateInput(c.dueDate)}
              onChange={(e) => { onSetDue(c, e.target.value); setShowDue(false); }}
              onBlur={() => setShowDue(false)}
              className="text-[11px] rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-1.5 py-0.5"
            />
          ) : due ? (
            <button
              onClick={() => setShowDue(true)}
              title="Change due date"
              className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${overdue ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'}`}
            >
              {overdue ? 'OVERDUE ' : ''}{due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </button>
          ) : (
            <button
              onClick={() => setShowDue(true)}
              className="px-2 py-0.5 rounded-md text-[10px] font-bold border border-dashed border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500 hover:border-primary-blue hover:text-primary-blue"
            >
              + Due
            </button>
          )}
          {c.subtaskTotal > 0 && (
            <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
              ✓ {c.subtaskDone}/{c.subtaskTotal}
            </span>
          )}
          {(c.assignees || []).map((a, i) => (
            <span key={i} className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300">{a}</span>
          ))}
          {(c.labels || []).map((l, i) => (
            <span key={`l${i}`} className="px-2 py-0.5 rounded-md text-[10px] font-bold text-white" style={{ backgroundColor: l.color || '#9ca3af' }}>{l.text}</span>
          ))}
        </div>
      </td>
    </tr>
  );
};

const BoardCard = ({ board }) => {
  const isColorBg = board.background?.type === 'color';
  const bgValue = board.background?.value;
  const isDefaultBg = !board.background || (isColorBg && (bgValue === '#ffffff' || bgValue === '#fff' || bgValue === ''));

  const boardStyle = board.background?.type === 'image'
    ? { backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(${board.background.value})` }
    : (!isDefaultBg && bgValue)
      ? { backgroundColor: bgValue }
      : {};

  const getContrastColor = (hexColor) => {
    if (!hexColor || hexColor === '#ffffff' || hexColor === '#fff') return 'text-gray-900 dark:text-gray-100';
    const hex = hexColor.replace('#', '');
    let r = 255, g = 255, b = 255;
    if (hex.length === 3) {
      r = parseInt(hex.substr(0, 1) + hex.substr(0, 1), 16);
      g = parseInt(hex.substr(1, 1) + hex.substr(1, 1), 16);
      b = parseInt(hex.substr(2, 1) + hex.substr(2, 1), 16);
    } else if (hex.length === 6) {
      r = parseInt(hex.substr(0, 2), 16);
      g = parseInt(hex.substr(2, 2), 16);
      b = parseInt(hex.substr(4, 2), 16);
    }
    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    return (yiq >= 128) ? 'text-gray-900' : 'text-white';
  };

  const textColorClass = board.background?.type === 'image'
    ? 'text-white'
    : isDefaultBg
      ? 'text-gray-900 dark:text-gray-100'
      : getContrastColor(bgValue);

  const getTypeIcon = () => {
    switch (board.type) {
      case 'social': return '📢';
      case 'contacts': return '👤';
      default: return '📝';
    }
  };

  return (
    <Link
      to={`/boards/${board.slug || board._id}`}
      style={boardStyle}
      className={`group relative h-36 rounded-2xl border p-5 transition-all hover:shadow-2xl hover:-translate-y-1.5 overflow-hidden bg-cover bg-center flex flex-col justify-between ${
        board.background?.type === 'image' || !isDefaultBg
          ? 'border-none shadow-md'
          : 'bg-white dark:bg-gray-900 border-gray-100 dark:border-gray-800'
      } ${textColorClass}`}
    >
      <div className="absolute top-4 right-4 text-xl opacity-20 group-hover:opacity-100 transition-opacity">
        {getTypeIcon()}
      </div>
      <div className="relative z-10">
        <h3 className="font-black text-lg leading-tight group-hover:underline uppercase tracking-tighter pr-8">{board.name}</h3>
        {board.description && <p className="text-[10px] mt-1 opacity-80 line-clamp-1 font-bold italic">"{board.description}"</p>}
      </div>

      <div className="relative z-10 flex justify-between items-center mt-auto">
        <div className="flex items-center space-x-3">
          {board.owner && (
            <div className="h-6 w-6 rounded-full overflow-hidden border border-white/30 shadow-sm flex-shrink-0" title={`Owner: ${board.owner.name}`}>
              {board.owner.avatar ? (
                <img src={getFileUrl(board.owner.avatar)} alt="" className="h-full w-full object-cover" />
              ) : (
                <div className="h-full w-full bg-white/20 flex items-center justify-center text-[8px] font-black">{board.owner.name?.charAt(0)}</div>
              )}
            </div>
          )}
          <div className="flex flex-col">
            <span className="text-[9px] uppercase font-black tracking-widest opacity-60">
              Status
            </span>
            <span className="text-[10px] font-black uppercase tracking-tighter">
              {board.columns?.length || 0} Phases
            </span>
          </div>
        </div>
        <div className="h-8 w-8 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-md border border-white/30 opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:rotate-12">
          <svg className="w-5 h-5 text-current" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M9 5l7 7-7 7"></path></svg>
        </div>
      </div>

      {board.background?.type === 'image' && (
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity"></div>
      )}
    </Link>
  );
};

export default Dashboard;
